import { INDEFINITE_END_DATE } from 'utils/date';
import { ContractDelta } from 'types/scenario';
import moment from 'moment';
import { Coworker } from 'types/coworker';

/**
 * Utility function to get the adjusted contract start and end dates based on coworker and contract deltas.
 * @param coworkerDelta - Partial coworker object containing potential contract date changes.
 * @param contractDeltas - Array of contract delta objects representing changes to contract periods.
 * @param contractStartDate - The original contract start date of the coworker.
 * @param contractEndDate - The original contract end date of the coworker.
 * @returns An object containing the new adjusted contract start and end dates.
 * Original: 2024-01-01 to 2024-12-31
 * Contract deltas: [{start: '2023-12-01', end: '2024-06-01'}]
 * Coworker delta: {contractStartDate: '2024-02-01', contractEndDate: '2025-01-31'}
 * Result:
 * newContractStartDate: '2023-12-01' (from contract delta - earliest)
 * newContractEndDate: '2025-01-31' (from coworker delta - latest)
 */
export const getCoworkerDelta = (
    coworkerDelta : Partial<Coworker> | undefined,
    contractDeltas: ContractDelta[] | undefined,
    contractStartDate: string,
    contractEndDate: string,
) => {
    let deltaContractStartDate :string = contractStartDate;
    let deltaContractEndDate :string = contractEndDate;

    if (contractDeltas?.length) {
        // eslint-disable-next-line prefer-destructuring
        deltaContractStartDate = contractDeltas
            .map(el => el.startDate)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
        const latestContractDelta = contractDeltas.sort(
            (a, b) => new Date(a.endDate ?? INDEFINITE_END_DATE).getTime() - new Date(b.endDate ?? INDEFINITE_END_DATE).getTime(),
        )[contractDeltas.length - 1];
        deltaContractEndDate = latestContractDelta?.endDate ?? INDEFINITE_END_DATE;
    }

    let coworkerDeltaStartDate = contractStartDate;
    let coworkerDeltaEndDate = contractEndDate;
    if (coworkerDelta) {
        coworkerDeltaStartDate = coworkerDelta?.contractStartDate ?? contractStartDate;
        coworkerDeltaStartDate = moment(contractStartDate).isBefore(coworkerDeltaStartDate)
            ? contractStartDate
            : coworkerDeltaStartDate;

        coworkerDeltaEndDate = coworkerDelta?.contractEndDate ?? contractEndDate;
        coworkerDeltaEndDate = moment(contractEndDate).isAfter(coworkerDeltaEndDate)
            ? contractEndDate
            : coworkerDeltaEndDate;
    }

    return {
        newContractStartDate: moment(coworkerDeltaStartDate).isAfter(deltaContractStartDate)
            ? deltaContractStartDate
            : contractStartDate,
        newContractEndDate: moment(coworkerDeltaEndDate ?? INDEFINITE_END_DATE).isBefore(
            deltaContractEndDate ?? INDEFINITE_END_DATE,
        )
            ? deltaContractEndDate
            : coworkerDeltaEndDate,
    };
};
